import { createRoutine } from 'redux-saga-routines'
import {
  call,
  debounce,
  take,
  delay,
  fork,
  put,
  select,
  takeEvery,
  takeLatest,
  takeLeading,
  spawn
} from '@redux-saga/core/effects'
import { eventChannel } from 'redux-saga'
import { head, pluck, propOr } from 'ramda'
import { isFalsy } from 'ramda-adjunct'
import ExchangeService from 'src/services/ExchangeService'
import DictionariesService from 'src/services/DictionariesService'
import LaravelEchoService from 'src/services/LaravelEchoService'
import {
  ERROR_DELAY_TIME,
  EXCHANGE_TYPES,
  EXCHANGE_EVENT_TYPES
} from 'src/ducks/consts'
import { selectDictionaries } from 'src/ducks/selectors'
import { selectCurrentUserId } from 'src/features/account/duck/selectors'
import {
  selectExportFilters,
  selectExportFiltersFormatted,
  selectExportFiltersTabsList,
  selectExportActiveTab,
  selectIsExportOffersLoading,
  selectExportTabsData
} from 'src/features/stocks/ducks/selectors'
import { setSnackbarValues } from 'src/ducks/actions'
import { showAlertToast } from 'src/components/Toast'
import translate from 'src/intl/translate'
import {
  displayResponseErrorMessage,
  getErrorMessageFromApiResponse
} from 'src/utils/helpers'
import { normalizeToLocationsIds, normalizeToCompaniesIds } from './normalizers'
import {
  clearOfferList,
  getOfferListRollbackView,
  isOfferListRateLimitActive,
  registerOfferListRateLimit,
  restoreOfferListView
} from './offerListRateLimit'

export const cleanExportOffersDetailsRoutine = createRoutine(
  'CLEAN_EXPORT_OFFERS_DETAILS'
)
export const getExportOffersRoutine = createRoutine('GET_EXPORT_OFFERS')
export const clearExportOffersRoutine = createRoutine('CLEAR_EXPORT_OFFERS')
export const showExportOfferDetailsRoutine = createRoutine(
  'SHOW_EXPORT_OFFER_DETAILS'
)
export const setExportDrawerRoutine = createRoutine('SET_EXPORT_DRAWER')
export const setExportFiltersRoutine = createRoutine('SET_EXPORT_FILTERS')
export const updateExportOfferRoutine = createRoutine('UPDATE_EXPORT_OFFER')
export const reserveExportOfferRoutine = createRoutine('RESERVE_EXPORT_OFFER')
export const refreshExportOfferRoutine = createRoutine('REFRESH_EXPORT_OFFER')
export const makeExportOfferPublicRoutine = createRoutine(
  'MAKE_EXPORT_OFFER_PUBLIC'
)
export const clearExportFiltersRoutine = createRoutine('CLEAR_EXPORT_FILTERS')
export const getExportFilterTabsListRoutine = createRoutine(
  'GET_EXPORT_FILTER_TABS_LIST'
)
export const createExportFilterTabRoutine = createRoutine(
  'CREATE_EXPORT_FILTER_TAB'
)
export const removeExportFilterTabRoutine = createRoutine(
  'REMOVE_EXPORT_FILTER_TAB'
)
export const setExportActiveTabRoutine = createRoutine('SET_EXPORT_ACTIVE_TAB')

export const EXPORT_TAB_EVENT_OFFER_CREATED = 'EXPORT_TAB_EVENT_OFFER_CREATED'
export const EXPORT_TAB_EVENT_OFFER_REFRESHED =
  'EXPORT_TAB_EVENT_OFFER_REFRESHED'
export const EXPORT_TAB_EVENT_OFFER_DELETED = 'EXPORT_TAB_EVENT_OFFER_DELETED'
export const EXPORT_OFFERS_LIST_UPDATED_BY_TAB_LIVE_DATA =
  'EXPORT_OFFERS_LIST_UPDATED_BY_TAB_LIVE_DATA'
export const exportOffersCleanerRoutine = createRoutine('EXPORT_OFFERS_CLEANER')
export const bidExportOfferRoutine = createRoutine('BID_EXPORT_OFFER')

const EXPORT_FILTERS_CHANGED = 'EXPORT_FILTERS_CHANGED'
const FILTERS_DEBOUNCE_MS = 400
let exportFiltersRevision = 0
let exportFiltersRollbackView = null
let exportFiltersRequestInProgress = false

const invalidateExportFiltersDebounce = () => {
  exportFiltersRevision += 1
  exportFiltersRollbackView = null
}

//actions

function* getExportOffers() {
  const isLoading = yield select(selectIsExportOffersLoading)

  if (isLoading || isOfferListRateLimitActive()) {
    return { ignored: true }
  }
  try {
    const activeTab = yield select(selectExportActiveTab)

    //case when initially loads
    if (isFalsy(activeTab)) {
      yield call(getExportFilterTabsList)
      const tabs = yield select(selectExportFiltersTabsList)
      return yield call(setExportActiveTab, {
        payload: { id: propOr('', 'id', head(tabs)) }
      })
    }

    yield put(getExportOffersRoutine.request())

    const tabs = yield select(selectExportFiltersTabsList)
    const filters = yield select(selectExportFiltersFormatted)
    const requestedView = {
      activeTab,
      filters: yield select(selectExportFilters)
    }

    const data = yield call(
      ExchangeService.getExportOffersListByTab,
      isFalsy(activeTab) ? head(tabs).id : activeTab,
      filters
    )

    yield put(getExportOffersRoutine.success(data))
    exportFiltersRollbackView = requestedView
    yield call(getExportFilterTabsList)

    return { success: true }
  } catch (e) {
    const rateLimit = registerOfferListRateLimit(e)

    if (rateLimit) {
      yield put(getExportOffersRoutine.failure())
      yield call(
        showAlertToast,
        translate().formatMessage(
          { id: 'offers.offerListRateLimited' },
          { seconds: rateLimit.retryAfter }
        ),
        { id: 'offer-list-rate-limit', duration: 5000 }
      )

      return { rateLimited: true }
    }

    yield put(getExportOffersRoutine.failure())
    console.log(e)

    return { failed: true }
  }
}

function* clearExportOffers() {
  yield put(clearExportOffersRoutine.success())
}

function* cleanExportOffersDetails() {
  yield put(cleanExportOffersDetailsRoutine.success())
}

function* showExportOfferDetails({ payload }) {
  yield put(showExportOfferDetailsRoutine.request())
  try {
    const { data } = yield call(ExchangeService.showExportOffer, payload)
    yield put(showExportOfferDetailsRoutine.success(data))
  } catch (e) {
    const message = getErrorMessageFromApiResponse(e)
    yield put(showExportOfferDetailsRoutine.failure({ message }))
    yield delay(ERROR_DELAY_TIME)
  } finally {
    yield put(showExportOfferDetailsRoutine.fulfill())
  }
}

function* setExportDrawer({ payload }) {
  yield put(setExportDrawerRoutine.success(payload))
}

function* setExportFilters({ payload }) {
  if (isOfferListRateLimitActive()) {
    return
  }

  const dictionaries = yield select(selectDictionaries)
  const activeTab = yield select(selectExportActiveTab)

  if (!exportFiltersRollbackView) {
    exportFiltersRollbackView = {
      activeTab,
      filters: yield select(selectExportFilters)
    }
  }

  exportFiltersRevision += 1
  const revision = exportFiltersRevision

  yield put(setExportFiltersRoutine.success({ ...payload, dictionaries }))
  yield put({
    type: EXPORT_FILTERS_CHANGED,
    payload: { activeTab, revision }
  })
}

function* getExportOffersAfterFiltersChange({ payload }) {
  if (
    payload.revision !== exportFiltersRevision ||
    payload.activeTab !== (yield select(selectExportActiveTab))
  ) {
    return
  }

  while (
    exportFiltersRequestInProgress ||
    (yield select(selectIsExportOffersLoading))
  ) {
    yield delay(50)
  }

  if (
    payload.revision !== exportFiltersRevision ||
    payload.activeTab !== (yield select(selectExportActiveTab))
  ) {
    return
  }

  const rollbackView = exportFiltersRollbackView

  if (isOfferListRateLimitActive()) {
    if (rollbackView) {
      yield put(
        restoreOfferListView(
          EXCHANGE_TYPES.EXPORT,
          rollbackView.activeTab,
          rollbackView.filters
        )
      )
    }
    exportFiltersRollbackView = null
    return
  }

  exportFiltersRequestInProgress = true

  try {
    const result = yield* getExportOffers()

    if (result?.rateLimited && rollbackView) {
      yield put(
        restoreOfferListView(
          EXCHANGE_TYPES.EXPORT,
          rollbackView.activeTab,
          rollbackView.filters
        )
      )
    }
  } finally {
    exportFiltersRequestInProgress = false
    if (payload.revision === exportFiltersRevision) {
      exportFiltersRollbackView = null
    }
  }
}

function* clearExportFilters() {
  const isLoading = yield select(selectIsExportOffersLoading)

  if (isLoading || isOfferListRateLimitActive()) {
    return
  }

  const previousActiveTab = yield select(selectExportActiveTab)
  const previousFilters = yield select(selectExportFilters)
  const rollbackView = getOfferListRollbackView(
    exportFiltersRollbackView,
    previousActiveTab,
    previousFilters
  )

  invalidateExportFiltersDebounce()

  yield put(clearExportFiltersRoutine.success())
  const result = yield* getExportOffers()

  if (result?.rateLimited) {
    yield put(
      restoreOfferListView(
        EXCHANGE_TYPES.EXPORT,
        rollbackView.activeTab,
        rollbackView.filters
      )
    )
  }
}

function* updateExportOffer({ payload }) {
  yield put(updateExportOfferRoutine.request())
  try {
    const { data } = yield call(
      ExchangeService.updateExportOffer,
      payload.id,
      payload.values
    )
    yield put(updateExportOfferRoutine.success(data))
  } catch (e) {
    const message = getErrorMessageFromApiResponse(e)
    yield put(updateExportOfferRoutine.failure({ message }))
    yield delay(ERROR_DELAY_TIME)
  } finally {
    yield put(updateExportOfferRoutine.fulfill())
  }
}

function* reserveExportOffer({ payload }) {
  yield put(reserveExportOfferRoutine.request())
  try {
    yield call(ExchangeService.reserveExportOffer, payload.id)
    yield put(reserveExportOfferRoutine.success())
    yield* showExportOfferDetails({ payload })
  } catch (e) {
    const message = getErrorMessageFromApiResponse(e)
    yield put(reserveExportOfferRoutine.failure({ message }))
    yield delay(ERROR_DELAY_TIME)
  } finally {
    yield put(reserveExportOfferRoutine.fulfill())
  }
}

function* bidExportOffer({ payload }) {
  yield put(bidExportOfferRoutine.request())
  try {
    yield call(ExchangeService.bidExportOffer, payload.id, {
      price: payload.price
    })
    yield put(bidExportOfferRoutine.success())
    yield call(showExportOfferDetails, { payload: { id: payload.id } })
    yield fork(setSnackbarValues, {
      payload: {
        message: translate().formatMessage({
          id: 'makeABid.successMessage'
        })
      }
    })
  } catch (e) {
    const message = getErrorMessageFromApiResponse(e)
    yield put(bidExportOfferRoutine.failure({ message }))
    yield call(displayResponseErrorMessage, e)
  } finally {
    yield delay(ERROR_DELAY_TIME)
    yield put(bidExportOfferRoutine.fulfill())
  }
}

function* refreshExportOffer({ payload }) {
  yield put(refreshExportOfferRoutine.request())
  try {
    yield call(ExchangeService.refreshExportOffer, payload.id)
    yield put(refreshExportOfferRoutine.success())
    yield* showExportOfferDetails({ payload })
    yield fork(setSnackbarValues, {
      payload: {
        message: translate().formatMessage({
          id: 'myOffers.refreshOfferSuccessMessage'
        })
      }
    })
  } catch (e) {
    const message = getErrorMessageFromApiResponse(e)
    yield put(refreshExportOfferRoutine.failure({ message }))
    yield call(displayResponseErrorMessage, e)
  } finally {
    yield delay(ERROR_DELAY_TIME)
    yield put(refreshExportOfferRoutine.fulfill())
  }
}

function* makeExportOfferPublic({ payload }) {
  yield put(makeExportOfferPublicRoutine.request())
  try {
    yield call(ExchangeService.makeExportOfferPublic, payload.id)
    yield put(makeExportOfferPublicRoutine.success())
    yield* showExportOfferDetails({ payload })
    yield fork(setSnackbarValues, {
      payload: {
        message: translate().formatMessage({
          id: 'myOffers.makeOfferPublicSuccessMessage'
        })
      }
    })
  } catch (e) {
    const message = getErrorMessageFromApiResponse(e)
    yield put(makeExportOfferPublicRoutine.failure({ message }))
    yield call(displayResponseErrorMessage, e)
  } finally {
    yield delay(ERROR_DELAY_TIME)
    yield put(makeExportOfferPublicRoutine.fulfill())
  }
}

function listenToExchangeTabEvents(data) {
  return eventChannel(emitter => {
    LaravelEchoService.getInstance().listenToExchangeTabs({
      exchangeType: data.exchangeType,
      userId: data.userId,
      tabsIds: data.tabsIds,
      callback: (tabId, event, payload) => {
        switch (event) {
          case EXCHANGE_EVENT_TYPES.OFFER_CREATED:
            return emitter({
              type: EXPORT_TAB_EVENT_OFFER_CREATED,
              tabId,
              payload
            })
          case EXCHANGE_EVENT_TYPES.OFFER_REFRESHED:
            return emitter({
              type: EXPORT_TAB_EVENT_OFFER_REFRESHED,
              tabId,
              payload
            })
          case EXCHANGE_EVENT_TYPES.OFFER_DELETED:
            return emitter({
              type: EXPORT_TAB_EVENT_OFFER_DELETED,
              tabId,
              payload
            })
          default:
            //handle information about not handled type
            return null
        }
      }
    })
    // unsubscribe function
    return () => {
      // do whatever to interrupt the socket communication here
    }
  })
}

export function* getExportFilterTabsList() {
  try {
    const tabs = yield call(ExchangeService.getExportFilterTabsList)
    const locationsIds = normalizeToLocationsIds(tabs.data)
    const companiesIds = normalizeToCompaniesIds(tabs.data)

    const locations = yield call(DictionariesService.locations, {
      filter: {
        id: locationsIds.map(id => head(id.split('|')))
      }
    })
    const companies = yield call(DictionariesService.companies, {
      filter: {
        id: companiesIds
      }
    })

    yield put(
      getExportFilterTabsListRoutine.success({
        data: tabs.data,
        locations: locations.data,
        companies: companies.data
      })
    )

    //listen to tabs events
    const userId = yield select(selectCurrentUserId)
    yield spawn(listenToExchangeTabEventsWatcher, {
      exchangeType: EXCHANGE_TYPES.EXPORT,
      userId,
      tabsIds: pluck('id')(tabs.data)
    })
  } catch (e) {
    console.log(e)
  }
}

function* createExportFilterTab({ payload }) {
  try {
    invalidateExportFiltersDebounce()
    const filters = yield select(selectExportFilters)
    const activeTab = yield select(selectExportActiveTab)
    const tabs = yield select(selectExportFiltersTabsList)
    const activeTabData = head(tabs.filter(v => v.id === activeTab))

    const { data } = yield call(ExchangeService.createExportFilterTab, {
      name: payload.empty ? null : filters.tabName
    })

    yield put(
      createExportFilterTabRoutine.success({
        data,
        currentFilters: payload.empty ? null : activeTabData.currentFilters
      })
    )
    //set active new tab

    yield call(setExportActiveTab, { payload: { id: data.id } })
  } catch (e) {
    console.log(e)
  }
}

function* removeExportFilterTab({ payload }) {
  try {
    const isLoading = yield select(selectIsExportOffersLoading)

    if (isLoading || isOfferListRateLimitActive()) {
      return
    }

    const activeTab = yield select(selectExportActiveTab)
    invalidateExportFiltersDebounce()

    yield call(ExchangeService.removeExportFilterTab, payload.id)
    yield put(removeExportFilterTabRoutine.success({ id: payload.id }))
    //after removal set active tab to first one
    const tabs = yield select(selectExportFiltersTabsList)
    yield call(setExportActiveTab, {
      payload: {
        id: head(tabs).id,
        clearOnRateLimit: activeTab === payload.id
      }
    })
  } catch (e) {
    console.log(e)
  }
}

function* setExportActiveTab({ payload }) {
  try {
    const isLoading = yield select(selectIsExportOffersLoading)
    const previousActiveTab = yield select(selectExportActiveTab)

    if (
      isLoading ||
      isOfferListRateLimitActive() ||
      previousActiveTab === payload.id
    ) {
      return
    }
    const previousFilters = yield select(selectExportFilters)
    const rollbackView = getOfferListRollbackView(
      exportFiltersRollbackView,
      previousActiveTab,
      previousFilters
    )

    invalidateExportFiltersDebounce()
    const tabs = yield select(selectExportFiltersTabsList)
    const tab = head(tabs.filter(v => v.id === payload.id))
    const tabsData = yield select(selectExportTabsData)

    yield put(
      setExportActiveTabRoutine.success({
        activeTab: tab.id,
        filters: tab.currentFilters
      })
    )
    const result = yield call(getExportOffers)

    if (result?.rateLimited) {
      if (payload.clearOnRateLimit) {
        yield put(clearOfferList(EXCHANGE_TYPES.EXPORT))
      } else {
        yield put(
          restoreOfferListView(
            EXCHANGE_TYPES.EXPORT,
            rollbackView.activeTab,
            rollbackView.filters
          )
        )
      }

      return result
    }

    //if tab has live data stored move them to offers list
    if (!isFalsy(tabsData[tab.id])) {
      yield put({
        type: EXPORT_OFFERS_LIST_UPDATED_BY_TAB_LIVE_DATA,
        payload: { tabId: tab.id, tabData: tabsData[tab.id] }
      })
    }

    return result
  } catch (e) {
    console.log(e)
  }
}

function* exportOffersCleaner() {
  yield put(exportOffersCleanerRoutine.success())
}

function* getExportOffersManually() {
  const activeTab = yield select(selectExportActiveTab)
  const filters = yield select(selectExportFilters)
  const rollbackView = getOfferListRollbackView(
    exportFiltersRollbackView,
    activeTab,
    filters
  )

  invalidateExportFiltersDebounce()
  const result = yield* getExportOffers()

  if (result?.rateLimited) {
    yield put(
      restoreOfferListView(
        EXCHANGE_TYPES.EXPORT,
        rollbackView.activeTab,
        rollbackView.filters
      )
    )
  }
}

//watchers

function* getExportOffersRoutineWatcher() {
  yield takeLeading(getExportOffersRoutine.TRIGGER, getExportOffersManually)
}

function* cleanExportOffersDetailsRoutineWatcher() {
  yield takeLatest(
    cleanExportOffersDetailsRoutine.TRIGGER,
    cleanExportOffersDetails
  )
}

function* clearExportOffersRoutineWatcher() {
  yield takeLatest(clearExportOffersRoutine.TRIGGER, clearExportOffers)
}

function* showExportOfferDetailsRoutineWatcher() {
  yield takeLatest(
    showExportOfferDetailsRoutine.TRIGGER,
    showExportOfferDetails
  )
}

function* setExportDrawerRoutineWatcher() {
  yield takeLatest(setExportDrawerRoutine.TRIGGER, setExportDrawer)
}

function* setExportFiltersRoutineWatcher() {
  yield takeEvery(setExportFiltersRoutine.TRIGGER, setExportFilters)
}

function* exportFiltersDebounceWatcher() {
  yield debounce(
    FILTERS_DEBOUNCE_MS,
    EXPORT_FILTERS_CHANGED,
    getExportOffersAfterFiltersChange
  )
}

function* updateExportOfferRoutineWatcher() {
  yield takeLatest(updateExportOfferRoutine.TRIGGER, updateExportOffer)
}

function* reserveExportOfferRoutineWatcher() {
  yield takeLatest(reserveExportOfferRoutine.TRIGGER, reserveExportOffer)
}

function* bidExportOfferRoutineWatcher() {
  yield takeLatest(bidExportOfferRoutine.TRIGGER, bidExportOffer)
}

function* refreshExportOfferRoutineWatcher() {
  yield takeLatest(refreshExportOfferRoutine.TRIGGER, refreshExportOffer)
}

function* makeExportOfferPublicRoutineWatcher() {
  yield takeLatest(makeExportOfferPublicRoutine.TRIGGER, makeExportOfferPublic)
}

function* clearExportFiltersRoutineWatcher() {
  yield takeLeading(clearExportFiltersRoutine.TRIGGER, clearExportFilters)
}

function* getExportFilterTabsListRoutineWatcher() {
  yield takeLatest(
    getExportFilterTabsListRoutine.TRIGGER,
    getExportFilterTabsList
  )
}

function* createExportFilterTabRoutineWatcher() {
  yield takeLatest(createExportFilterTabRoutine.TRIGGER, createExportFilterTab)
}

function* removeExportFilterTabRoutineWatcher() {
  yield takeLatest(removeExportFilterTabRoutine.TRIGGER, removeExportFilterTab)
}

function* setExportActiveTabRoutineWatcher() {
  yield takeLeading(setExportActiveTabRoutine.TRIGGER, setExportActiveTab)
}

function* listenToExchangeTabEventsWatcher(payload) {
  const channel = yield call(listenToExchangeTabEvents, payload)
  while (true) {
    const action = yield take(channel)
    yield put(action)
  }
}

function* exportOffersCleanerRoutineWatcher() {
  yield takeLatest(exportOffersCleanerRoutine.TRIGGER, exportOffersCleaner)
}

export const exchangeExportSagas = [
  fork(getExportOffersRoutineWatcher),
  fork(clearExportOffersRoutineWatcher),
  fork(cleanExportOffersDetailsRoutineWatcher),
  fork(showExportOfferDetailsRoutineWatcher),
  fork(setExportDrawerRoutineWatcher),
  fork(setExportFiltersRoutineWatcher),
  fork(exportFiltersDebounceWatcher),
  fork(updateExportOfferRoutineWatcher),
  fork(reserveExportOfferRoutineWatcher),
  fork(refreshExportOfferRoutineWatcher),
  fork(clearExportFiltersRoutineWatcher),
  fork(getExportFilterTabsListRoutineWatcher),
  fork(createExportFilterTabRoutineWatcher),
  fork(removeExportFilterTabRoutineWatcher),
  fork(setExportActiveTabRoutineWatcher),
  fork(exportOffersCleanerRoutineWatcher),
  fork(bidExportOfferRoutineWatcher),
  fork(makeExportOfferPublicRoutineWatcher)
]
